import { cn } from "@monbits/ui";
import { Activity } from "lucide-react";
import type * as React from "react";
import type { AuditLog } from "src/types";
import { getCategoryConfig } from "@/utils/audit-log";

interface AuditLogTimelineProps {
	logs: AuditLog[];
	isLoading?: boolean;
	className?: string;
}

const formatRelativeTime = (dateStr: string): string => {
	const date = new Date(dateStr);
	const now = new Date();
	const diffMs = now.getTime() - date.getTime();
	const diffMins = Math.floor(diffMs / 60000);
	const diffHours = Math.floor(diffMins / 60);
	const diffDays = Math.floor(diffHours / 24);

	if (diffMins < 1) return "Just now";
	if (diffMins < 60) return `${diffMins}m ago`;
	if (diffHours < 24) return `${diffHours}h ago`;
	if (diffDays < 7) return `${diffDays}d ago`;

	return date.toLocaleDateString("en-US", {
		month: "short",
		day: "numeric",
		year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
	});
};

const formatFullDate = (dateStr: string): string => {
	const date = new Date(dateStr);
	return date.toLocaleString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
};

export const AuditLogTimeline: React.FC<AuditLogTimelineProps> = ({
	logs,
	isLoading = false,
	className,
}) => {
	if (isLoading) {
		return (
			<div className={cn("space-y-3", className)}>
				{[...Array(3)].map((_, i) => (
					<div key={i} className="flex gap-3 animate-pulse">
						<div className="flex flex-col items-center">
							<div className="h-8 w-8 rounded-full bg-muted" />
							{i < 2 && <div className="w-px h-12 bg-muted mt-2" />}
						</div>
						<div className="flex-1 space-y-2">
							<div className="h-4 w-3/4 bg-muted rounded" />
							<div className="h-3 w-1/2 bg-muted rounded" />
						</div>
					</div>
				))}
			</div>
		);
	}

	if (logs.length === 0) {
		return (
			<div className={cn("text-center py-8", className)}>
				<div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-muted/50 mb-3">
					<Activity className="h-5 w-5 text-muted-foreground" />
				</div>
				<p className="text-sm text-muted-foreground">No activity yet</p>
			</div>
		);
	}

	return (
		<div className={cn("space-y-4", className)}>
			{logs.map((log, index) => {
				const config = getCategoryConfig(log.actionCategory);
				const Icon = config.icon;
				const isLast = index === logs.length - 1;

				return (
					<div key={log.id} className="flex gap-3 group">
						<div className="flex flex-col items-center flex-shrink-0">
							<div
								className="h-8 w-8 rounded-full border-2 border-background flex items-center justify-center transition-colors"
								style={{
									backgroundColor: `${config.color}15`,
									color: config.color,
								}}
							>
								<Icon className="h-3.5 w-3.5" />
							</div>
							{!isLast && (
								<div className="w-px h-full bg-border mt-2 min-h-6" />
							)}
						</div>

						<div className="flex-1 min-w-0">
							<div className="flex items-start justify-between gap-3 mb-1">
								<div className="flex-1 min-w-0">
									<p className="text-sm font-medium text-foreground">
										{log.description}
									</p>
									<div className="flex items-center gap-2 mt-0.5">
										{log.actor.email && (
											<span className="text-xs text-muted-foreground">
												{log.actor.email}
											</span>
										)}
										<span className="text-xs text-muted-foreground">•</span>
										<span
											className="text-xs text-muted-foreground"
											title={formatFullDate(log.createdAt)}
										>
											{formatRelativeTime(log.createdAt)}
										</span>
									</div>
								</div>

								<div
									className={cn(
										"text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0",
										log.status === "success" && "bg-green-100 text-green-700",
										log.status === "failure" && "bg-red-100 text-red-700",
										log.status === "partial" && "bg-yellow-100 text-yellow-700",
									)}
								>
									{log.status}
								</div>
							</div>

							{log.metadata && Object.keys(log.metadata).length > 0 ? (
								<div className="space-y-1 mt-2">
									{log.metadata.reason ? (
										<div className="text-xs text-muted-foreground">
											<span className="font-medium">Reason:</span>{" "}
											{String(log.metadata.reason)}
										</div>
									) : null}
									{log.metadata.error ? (
										<div className="text-xs text-red-600">
											<span className="font-medium">Error:</span>{" "}
											{String(log.metadata.error)}
										</div>
									) : null}
								</div>
							) : null}

							{log.changes && Object.keys(log.changes).length > 0 && (
								<details className="mt-2">
									<summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground select-none">
										View changes ({Object.keys(log.changes).length})
									</summary>
									<div className="mt-2 p-3 rounded-md bg-muted/50 space-y-2 text-xs">
										{Object.entries(log.changes).map(([key, value]) => (
											<div
												key={key}
												className="grid grid-cols-[auto_1fr] gap-2"
											>
												<span className="font-medium text-muted-foreground">
													{key}:
												</span>
												<span className="break-words">
													{JSON.stringify(value)}
												</span>
											</div>
										))}
									</div>
								</details>
							)}
						</div>
					</div>
				);
			})}
		</div>
	);
};
